import React from 'react';
import { Input, Textarea, Tooltip } from '@fluentui/react-components';
import { Delete16Regular, Info16Regular } from '@fluentui/react-icons';
import styles from './WorkspaceSetupWizard.module.css';
import {
    COMPANY_VALUE_BEHAVIOR_PLACEHOLDER,
    COMPANY_VALUE_DESCRIPTION_PLACEHOLDER,
    COMPANY_VALUE_NAME_PLACEHOLDER,
    PULSE_FIELD_META,
    PULSE_PREFERENCE_KEYS,
} from '../../utils/workspacePulseProfile';

const WorkspaceSetupStep6ValuesPulse = ({
    workspaceNameLabel,
    savedCompanyValues,
    valueDraft,
    onValueDraftChange,
    editingValueIndex,
    onEditSavedValue,
    onDeleteSavedValue,
    onSaveValueAndAddAnother,
    valueSaving,
    draftPulseProfile,
    savedPulseProfile,
    editingPulseProfile,
    onStartEditPulseField,
    onUpdatePulseField,
}) => {
    const renderPulseField = fieldKey => {
        const meta = PULSE_FIELD_META[fieldKey];
        const isEditing = editingPulseProfile[fieldKey] || !savedPulseProfile[fieldKey];

        return (
            <div key={fieldKey} className={styles.pulseGridItem}>
                <div className={styles.pulseFieldLabelRow}>
                    <p className={`${styles.fieldLabel} mb-0`}>{meta.label}</p>
                    <Tooltip positioning="above" withArrow content={meta.description}>
                        <Info16Regular color="var(--Neutral-Gray-6)" role="button" tabIndex={0} className={styles.pulseInfoIcon} aria-label={`${meta.label} info`} />
                    </Tooltip>
                </div>
                {isEditing ? (
                    <select
                        className={styles.pulseSelect}
                        value={draftPulseProfile[fieldKey]}
                        onChange={e => onUpdatePulseField(fieldKey, e.target.value)}
                        aria-label={meta.label}>
                        <option value="">Select</option>
                        {meta.options.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <div className={styles.pulseSavedRow}>
                        <p className={styles.pulseSavedValue}>{savedPulseProfile[fieldKey] || '—'}</p>
                        {savedPulseProfile[fieldKey] ? (
                            <button type="button" className={styles.changeLink} onClick={() => onStartEditPulseField(fieldKey)}>
                                Edit
                            </button>
                        ) : null}
                    </div>
                )}
            </div>
        );
    };

    const canSaveValue = (valueDraft?.name || '').trim().length > 0;

    return (
        <div style={{ maxWidth: '720px' }}>
            <p className={styles.step}>{`Values & Lens Pulse  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>
                Define your values and Lens Pulse preferences <span className={styles.stepMeta}>(Optional)</span>
            </h1>
            <p className={styles.stepDescription} style={{ marginBottom: '24px' }}>
                Company values and Lens Pulse settings help tailor coaching, briefings and intelligence across your workspace. You can update these at any time.
            </p>

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Company Values</h3>
                <p className={styles.step3Hint}>Add values that reflect how your organisation works. Each value can include a description and expected behaviour.</p>

                {savedCompanyValues.map((value, index) => {
                    if (editingValueIndex === index) return null;
                    return (
                        <div key={`${value.name}-${index}`} className={styles.valueCard}>
                            <div className={styles.orgSectionHeader}>
                                <p className={styles.valueCardName}>{value.name}</p>
                                <div className={styles.valueCardActions}>
                                    <button type="button" className={styles.changeLink} onClick={() => onEditSavedValue(index)}>
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.valueDeleteBtn}
                                        onClick={() => onDeleteSavedValue(index)}
                                        disabled={valueSaving}
                                        aria-label={`Delete ${value.name}`}>
                                        <Delete16Regular color="var(--Brand-Secondary, #c89741)" />
                                    </button>
                                </div>
                            </div>
                            {value.description ? <p className={styles.valueCardDescription}>{value.description}</p> : null}
                            {value.expected_behavior ? <p className={styles.valueCardBehavior}>{value.expected_behavior}</p> : null}
                        </div>
                    );
                })}

                <div className={styles.valueForm}>
                    <div className={styles.orgTextField}>
                        <p className={styles.fieldLabel}>Company Value Name</p>
                        <Input
                            className={styles.nameInput}
                            value={valueDraft.name}
                            onChange={(e, data) => onValueDraftChange('name', data.value)}
                            placeholder={COMPANY_VALUE_NAME_PLACEHOLDER}
                            appearance="outline"
                        />
                    </div>
                    <div className={styles.orgTextField}>
                        <p className={styles.fieldLabel}>Company Value Description</p>
                        <Textarea
                            className={styles.orgTextarea}
                            value={valueDraft.description}
                            onChange={(e, data) => onValueDraftChange('description', data.value)}
                            rows={4}
                            placeholder={COMPANY_VALUE_DESCRIPTION_PLACEHOLDER}
                        />
                    </div>
                    <div className={styles.orgTextField}>
                        <p className={styles.fieldLabel}>Expected Behaviour</p>
                        <Textarea
                            className={styles.orgTextarea}
                            value={valueDraft.expected_behavior}
                            onChange={(e, data) => onValueDraftChange('expected_behavior', data.value)}
                            rows={4}
                            placeholder={COMPANY_VALUE_BEHAVIOR_PLACEHOLDER}
                        />
                    </div>
                    <button
                        type="button"
                        className={styles.saveAndAddAnotherBtn}
                        disabled={!canSaveValue || valueSaving}
                        onClick={onSaveValueAndAddAnother}>
                        {valueSaving ? 'Saving…' : editingValueIndex !== null ? 'Save value' : 'Save and add another'}
                    </button>
                </div>
            </div>

            <hr className={styles.sectionDivider} aria-hidden="true" />

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Lens Pulse Preferences</h3>
                <div className={styles.pulsePreferencesGrid}>{PULSE_PREFERENCE_KEYS.map(renderPulseField)}</div>
            </div>
        </div>
    );
};

export default WorkspaceSetupStep6ValuesPulse;
